import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const COUNT_KEY = 'hypseya_welcome_count';
const getCount  = () => parseInt(localStorage.getItem(COUNT_KEY) || '0', 10);
const bumpCount = () => localStorage.setItem(COUNT_KEY, String(getCount() + 1));

// ── Slide content ─────────────────────────────────────────────────────────────
const SLIDES_STANDARD = [
  {
    eyebrow: 'Daily Loop',
    title: 'Your daily practice',
    body: 'Four phases built to develop emotional regulation — one session at a time.',
    visual: 'loop',
  },
  {
    eyebrow: 'ERI · Emotional Regulation Index',
    title: 'Your score grows with you',
    body: '0–100 across regulation, stability and consistency. Built by your daily practice.',
    visual: 'eri',
  },
  {
    eyebrow: 'Studio, Journal & Tools',
    title: 'Everything in one place',
    body: 'Guided meditations, somatic tools, journal and 8-week programs — your complete toolkit.',
    visual: 'explore',
  },
];

const SLIDES_MATRESCENCE = [
  {
    eyebrow: 'Daily Loop',
    title: 'Your daily practice',
    body: 'Orient, Practice, Observe, Integrate — four phases adapted for the matrescence journey.',
    visual: 'loop',
    warm: true,
  },
  {
    eyebrow: 'ERI · Emotional Regulation Index',
    title: 'Your score grows with you',
    body: '0–100 tracking your emotional regulation through the transition to motherhood.',
    visual: 'eri',
    warm: true,
  },
  {
    eyebrow: 'EPDS · Edinburgh Scale',
    title: 'Postnatal wellbeing check-ins',
    body: 'Bi-weekly postnatal screening — tracks your emotional wellbeing and flags when you need support.',
    visual: 'epds',
    warm: true,
  },
];

// ── Slide visuals ─────────────────────────────────────────────────────────────
function LoopVisual({ warm }) {
  const c = warm ? '#C4866A' : '#1E6B68';
  const steps = ['Orient', 'Practice', 'Observe', 'Integrate'];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', justifyContent: 'center' }}>
      {steps.map((s, i) => (
        <React.Fragment key={s}>
          <div style={{
            padding: '6px 12px',
            borderRadius: 20,
            background: `rgba(${warm ? '196,134,106' : '30,107,104'},0.12)`,
            border: `1px solid rgba(${warm ? '196,134,106' : '30,107,104'},0.28)`,
            fontSize: '0.72rem', fontWeight: 600,
            color: c, letterSpacing: '0.03em',
          }}>
            {s}
          </div>
          {i < 3 && <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.7rem' }}>→</span>}
        </React.Fragment>
      ))}
    </div>
  );
}

function ERIVisual({ warm }) {
  const color = warm ? '#C9A86C' : '#2D8B87';
  const r = 28, circ = 2 * Math.PI * r;
  const score = 72;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 18, justifyContent: 'center' }}>
      <div style={{ position: 'relative', width: 70, height: 70 }}>
        <svg width="70" height="70" viewBox="0 0 70 70">
          <circle cx="35" cy="35" r={r} stroke="rgba(255,255,255,0.07)" strokeWidth="4" fill="none"/>
          <circle cx="35" cy="35" r={r}
            stroke={color} strokeWidth="4" fill="none"
            strokeDasharray={circ}
            strokeDashoffset={circ * (1 - score / 100)}
            strokeLinecap="round"
            transform="rotate(-90 35 35)"
          />
        </svg>
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
        }}>
          <span style={{ fontSize: '1.1rem', fontWeight: 700, color, lineHeight: 1 }}>{score}</span>
          <span style={{ fontSize: '0.58rem', color: 'rgba(255,255,255,0.3)', marginTop: 1 }}>/100</span>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        {['Regulation', 'Stability', 'Consistency'].map((d, i) => (
          <div key={d} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <div style={{
              width: 48, height: 4, borderRadius: 2,
              background: `rgba(${warm ? '196,134,106' : '30,107,104'},0.15)`,
              overflow: 'hidden',
            }}>
              <div style={{
                width: `${[78, 65, 72][i]}%`, height: '100%',
                background: color, borderRadius: 2,
              }} />
            </div>
            <span style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.35)' }}>{d}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ExploreVisual() {
  const items = [
    { label: 'Studio', icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M1 8 C2.5 5.5 4 5.5 5 8 C6 10.5 7.5 10.5 8.5 8 C9.5 5.5 11 5.5 12.5 8 C13.5 9.5 14 9.5 14 8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" fill="none"/></svg> },
    { label: 'Journal', icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="3" y="2" width="9" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.2" fill="none"/><line x1="5.5" y1="6" x2="9.5" y2="6" stroke="currentColor" strokeWidth="1" strokeLinecap="round"/><line x1="5.5" y1="8.5" x2="9.5" y2="8.5" stroke="currentColor" strokeWidth="1" strokeLinecap="round"/></svg> },
    { label: 'Insights', icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><polyline points="1,12 4.5,7 7.5,9.5 11,4 13,6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" fill="none"/></svg> },
  ];
  return (
    <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
      {items.map(it => (
        <div key={it.label} style={{
          padding: '12px 16px',
          borderRadius: 14,
          background: 'rgba(30,107,104,0.1)',
          border: '1px solid rgba(30,107,104,0.2)',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', gap: 6,
          color: '#2D8B87',
        }}>
          {it.icon}
          <span style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.4)', fontWeight: 500 }}>{it.label}</span>
        </div>
      ))}
    </div>
  );
}

function EPDSVisual() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, justifyContent: 'center' }}>
      <div style={{
        width: 64, height: 64, borderRadius: '50%',
        border: '2px solid rgba(125,200,200,0.6)',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        background: 'rgba(125,200,200,0.06)',
      }}>
        <span style={{ fontSize: '1.2rem', fontWeight: 700, color: '#7EC8C8', lineHeight: 1 }}>8</span>
        <span style={{ fontSize: '0.58rem', color: 'rgba(255,255,255,0.28)' }}>/30</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        <div style={{
          padding: '4px 10px', borderRadius: 10,
          background: 'rgba(125,200,200,0.1)',
          border: '1px solid rgba(125,200,200,0.25)',
          fontSize: '0.65rem', color: '#7EC8C8', fontWeight: 600,
        }}>
          Low concern
        </div>
        <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.28)' }}>
          Bi-weekly check-in
        </div>
        <div style={{ fontSize: '0.65rem', color: 'rgba(196,134,106,0.6)' }}>
          Crisis flagging active
        </div>
      </div>
    </div>
  );
}

function SlideVisual({ visual, warm }) {
  if (visual === 'loop')    return <LoopVisual warm={warm} />;
  if (visual === 'eri')     return <ERIVisual warm={warm} />;
  if (visual === 'explore') return <ExploreVisual />;
  if (visual === 'epds')    return <EPDSVisual />;
  return null;
}

// ── Full bottom sheet (session 1) ─────────────────────────────────────────────
function FullSheet({ slides, warm, onDismiss, onGuide }) {
  const [slide, setSlide] = useState(0);
  const [animDir, setAnimDir] = useState(null);
  const accentColor = warm ? '#C4866A' : '#1E6B68';
  const accentRgb   = warm ? '196,134,106' : '30,107,104';
  const current = slides[slide];

  const goTo = (dir) => {
    setAnimDir(dir);
    setTimeout(() => {
      setSlide(s => s + dir);
      setAnimDir(null);
    }, 180);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.55)',
      backdropFilter: 'blur(4px)',
      zIndex: 8000,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-end',
    }}>
      <div style={{
        background: '#0E1A19',
        borderRadius: '28px 28px 0 0',
        border: `1px solid rgba(${accentRgb},0.18)`,
        borderBottom: 'none',
        padding: '0 0 40px',
        maxHeight: '82vh',
        overflow: 'hidden',
        animation: 'sheetUp 0.38s cubic-bezier(.2,.8,.4,1) forwards',
      }}>

        {/* Handle */}
        <div style={{ display: 'flex', justifyContent: 'center', padding: '14px 0 0' }}>
          <div style={{ width: 36, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.12)' }} />
        </div>

        {/* Header */}
        <div style={{ padding: '16px 28px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 22, height: 22, borderRadius: 6,
              background: `rgba(${accentRgb},0.15)`,
              border: `1px solid rgba(${accentRgb},0.3)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <circle cx="5" cy="5" r="3.5" stroke={accentColor} strokeWidth="1.3"/>
                <path d="M5 2V5L7 6.5" stroke={accentColor} strokeWidth="1" strokeLinecap="round"/>
              </svg>
            </div>
            <span style={{ fontSize: '0.7rem', color: `rgba(${accentRgb},0.75)`, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase' }}>
              Getting started
            </span>
          </div>
          <button
            onClick={onDismiss}
            aria-label="Close guide"
            style={{ background: 'none', border: 'none', padding: 10, cursor: 'pointer', color: 'rgba(255,255,255,0.45)' }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2 2L12 12M12 2L2 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* Slide content */}
        <div style={{
          padding: '28px 28px 24px',
          opacity: animDir ? 0 : 1,
          transform: animDir ? `translateX(${animDir > 0 ? '-20px' : '20px'})` : 'none',
          transition: 'opacity 0.18s, transform 0.18s',
        }}>
          {/* Eyebrow */}
          <p style={{ margin: '0 0 10px', fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: accentColor, opacity: 0.75 }}>
            {current.eyebrow}
          </p>

          {/* Title */}
          <h2 style={{
            margin: '0 0 10px',
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 'clamp(1.15rem, 4.5vw, 1.4rem)',
            color: 'rgba(255,255,255,0.9)',
            fontWeight: 500, lineHeight: 1.3,
          }}>
            {current.title}
          </h2>

          {/* Body */}
          <p style={{ margin: '0 0 26px', fontSize: '0.85rem', color: 'rgba(255,255,255,0.42)', lineHeight: 1.65 }}>
            {current.body}
          </p>

          {/* Visual */}
          <div style={{
            padding: '20px',
            background: 'rgba(255,255,255,0.03)',
            borderRadius: 18,
            border: '1px solid rgba(255,255,255,0.06)',
          }}>
            <SlideVisual visual={current.visual} warm={current.warm} />
          </div>
        </div>

        {/* Dot indicators */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 7, marginBottom: 24 }}>
          {slides.map((_, i) => (
            <div key={i} style={{
              width: slide === i ? 18 : 6,
              height: 6,
              borderRadius: 3,
              background: slide === i ? accentColor : 'rgba(255,255,255,0.12)',
              transition: 'all 0.3s',
            }} />
          ))}
        </div>

        {/* Navigation */}
        <div style={{ padding: '0 28px', display: 'flex', gap: 10 }}>
          {slide > 0 && (
            <button
              onClick={() => goTo(-1)}
              style={{
                padding: '13px 18px',
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 20,
                color: 'rgba(255,255,255,0.45)',
                fontSize: '0.85rem',
                cursor: 'pointer',
                outline: 'none',
              }}
            >
              ←
            </button>
          )}

          {slide < slides.length - 1 ? (
            <button
              onClick={() => goTo(1)}
              style={{
                flex: 1,
                padding: '13px',
                background: `rgba(${accentRgb},0.15)`,
                border: `1px solid rgba(${accentRgb},0.35)`,
                borderRadius: 20,
                color: accentColor,
                fontSize: '0.9rem',
                fontWeight: 600,
                cursor: 'pointer',
                outline: 'none',
              }}
            >
              Next →
            </button>
          ) : (
            <button
              onClick={onDismiss}
              style={{
                flex: 1,
                padding: '13px',
                background: `linear-gradient(135deg, ${warm ? '#C4866A' : '#1E6B68'} 0%, ${warm ? '#D4A5A0' : '#2D8B87'} 100%)`,
                border: 'none',
                borderRadius: 20,
                color: '#fff',
                fontSize: '0.9rem',
                fontWeight: 600,
                cursor: 'pointer',
                boxShadow: `0 6px 22px rgba(${accentRgb},0.3)`,
                outline: 'none',
              }}
            >
              Got it →
            </button>
          )}
        </div>

        {/* "See full guide" — secondary action, always accessible */}
        <button
          onClick={onGuide}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            margin: '12px auto 0',
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 20,
            color: 'rgba(255,255,255,0.62)',
            fontSize: '0.8rem',
            fontWeight: 600,
            cursor: 'pointer',
            padding: '8px 18px',
            letterSpacing: '0.01em',
          }}
        >
          See the full guide
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M2 6H10M7 3L10 6L7 9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>

      <style>{`
        @keyframes sheetUp {
          from { transform: translateY(40px); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
      `}</style>
    </div>
  );
}

// ── Compact banner (sessions 2–3) ─────────────────────────────────────────────
function CompactBanner({ warm, onDismiss, onGuide }) {
  const accentColor = warm ? '#C4866A' : '#1E6B68';
  const accentRgb   = warm ? '196,134,106' : '30,107,104';
  return (
    <div style={{
      margin: '0 0 16px',
      padding: '12px 14px',
      background: `rgba(${accentRgb},0.07)`,
      border: `1px solid rgba(${accentRgb},0.2)`,
      borderRadius: 14,
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      animation: 'fadeIn 0.4s ease forwards',
    }}>
      <div style={{
        width: 32, height: 32, borderRadius: '50%',
        background: `rgba(${accentRgb},0.12)`,
        border: `1px solid rgba(${accentRgb},0.25)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <circle cx="7" cy="7" r="5.5" stroke={accentColor} strokeWidth="1.2"/>
          <path d="M7 4.5V7.5" stroke={accentColor} strokeWidth="1.3" strokeLinecap="round"/>
          <circle cx="7" cy="9.5" r="0.6" fill={accentColor}/>
        </svg>
      </div>
      <div style={{ flex: 1 }}>
        <p style={{ margin: 0, fontSize: '0.8rem', color: 'rgba(255,255,255,0.65)', fontWeight: 500 }}>
          Still getting started?
        </p>
        <button
          onClick={onGuide}
          style={{
            margin: 0, padding: 0,
            background: 'none', border: 'none',
            fontSize: '0.72rem', color: accentColor,
            cursor: 'pointer', fontWeight: 600,
            textDecoration: 'underline', textUnderlineOffset: 2,
          }}
        >
          Open the guide →
        </button>
      </div>
      <button
        onClick={onDismiss}
        style={{
          background: 'none', border: 'none', padding: 4,
          cursor: 'pointer', color: 'rgba(255,255,255,0.2)', flexShrink: 0,
        }}
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M2 2L10 10M10 2L2 10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
        </svg>
      </button>
      <style>{`@keyframes fadeIn { from { opacity:0; transform:translateY(-6px);} to { opacity:1; transform:translateY(0);} }`}</style>
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────
export default function WelcomeSheet() {
  const navigate = useNavigate();
  const [count, setCount]       = useState(null);
  const [visible, setVisible]   = useState(false);
  const [mounted, setMounted]   = useState(false);

  const isMatrescence = localStorage.getItem('hypseya_user_mode') === 'matrescence';
  const slides = isMatrescence ? SLIDES_MATRESCENCE : SLIDES_STANDARD;
  const warm   = isMatrescence;

  useEffect(() => {
    const c = getCount();
    setCount(c);
    if (c < 3) {
      const t = setTimeout(() => { setMounted(true); setVisible(true); }, 900);
      return () => clearTimeout(t);
    }
  }, []);

  const dismiss = () => {
    setVisible(false);
    const prev = getCount();
    bumpCount();
    if (prev === 0) {
      setTimeout(() => {
        toast('Find the guide anytime in your profile →', {
          duration: 4500,
          style: { fontSize: '0.82rem' },
        });
      }, 400);
    }
    setTimeout(() => setMounted(false), 450);
  };

  const openGuide = () => {
    dismiss();
    navigate('/guide');
  };

  if (!mounted || count === null || count >= 3) return null;

  if (count === 0) {
    return visible
      ? <FullSheet slides={slides} warm={warm} onDismiss={dismiss} onGuide={openGuide} />
      : null;
  }

  // Sessions 2–3: compact banner (inline, not overlay)
  return visible
    ? <CompactBanner warm={warm} onDismiss={dismiss} onGuide={openGuide} />
    : null;
}
